import { Box, Stack, Typography } from "@mui/material";
import type { GridApiPremium, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import type {
  TabularDataCell,
  TabularRow,
  TabularSubtotalLabelPosition,
} from "../../../../../../../../api/types/svTypes.ts";
import FailedToLoadIcon from "../FailedToLoadIcon.tsx";
import GroupingCell from "../GroupingCell.tsx";
import GroupingLeafCell from "../GroupingLeafCell.tsx";
import MultipleColumnModeGroupingTotalCell from "../MultipleColumnModeGroupingTotalCell.tsx";
import SingleColumnModeGroupingTotalCell from "../SingleColumnModeGroupingTotalCell.tsx";
import {
  getFieldNameFromGroupingColumnByFieldGuid,
  type RowGroupingColumnModeType,
} from "./tabularGridDataProviderHelper.ts";

export const renderColumnHeader = (text: string | undefined, isFailedToLoad: boolean) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {isFailedToLoad && <FailedToLoadIcon title="The column could not be calculated." />}
      <Typography variant="caption" color="textSecondary" sx={{ lineHeight: "140%" }}>
        {text}
      </Typography>
    </Box>
  );
};

export const renderGroupingColumnHeader = (
  colDef: GridColDef<TabularRow, TabularDataCell>,
  rowGroupingColumnMode: RowGroupingColumnModeType,
  fieldIds: string[],
  apiRef: RefObject<GridApiPremium | null>,
  isColumnFailedToLoad: (fieldGuid: string) => boolean
) => {
  if (rowGroupingColumnMode === "multiple") {
    return renderColumnHeader(
      colDef.headerName,
      isColumnFailedToLoad(getFieldNameFromGroupingColumnByFieldGuid(colDef.field) ?? "")
    );
  }
  if (rowGroupingColumnMode === "single") {
    const failedIds = fieldIds.filter((id) => isColumnFailedToLoad(id));
    if (failedIds.length === 0) {
      return null;
    }
    return (
      <FailedToLoadIcon
        title={
          <Stack>
            {failedIds.map((id) => {
              const name = apiRef?.current?.getColumn(id)?.headerName ?? "";
              return (
                <Typography key={id} sx={{ fontSize: "0.688rem", fontWeight: 500 }}>
                  The column <b>{name}</b> could not be calculated.
                </Typography>
              );
            })}
          </Stack>
        }
      />
    );
  }
  return null;
};

export const handleRenderCell = (
  params: GridRenderCellParams<TabularRow, TabularDataCell>,
  rowGroupingColumnMode: RowGroupingColumnModeType,
  subtotalLabelPosition: TabularSubtotalLabelPosition,
  subtotalLabelFormatPrefix: string,
  subtotalLabelFormatSuffix: string
) => {
  if (rowGroupingColumnMode === "multiple") {
    return renderMultipleGroupedColumns(
      params,
      subtotalLabelPosition,
      subtotalLabelFormatPrefix,
      subtotalLabelFormatSuffix
    );
  }
  if (rowGroupingColumnMode === "single") {
    return renderSingleGroupedColumn(
      params,
      subtotalLabelPosition,
      subtotalLabelFormatPrefix,
      subtotalLabelFormatSuffix
    );
  }
  return null;
};

const renderSingleGroupedColumn = (
  params: GridRenderCellParams<TabularRow, TabularDataCell>,
  subtotalLabelPosition: TabularSubtotalLabelPosition,
  subtotalLabelFormatPrefix: string,
  subtotalLabelFormatSuffix: string
) => {
  if (params.rowNode.type === "group") {
    return (
      <GroupingCell
        id={params.id}
        rowNode={params.rowNode}
        containerProps={{ sx: { ml: params.rowNode.depth * 2 } }}
        typographyProps={{ variant: "body1" }}
      />
    );
  }
  if (params.rowNode.type === "leaf") {
    //Ignore leaf nodes
    return null;
  }
  if (subtotalLabelPosition !== "None" && params.rowNode.type === "footer") {
    return (
      <SingleColumnModeGroupingTotalCell
        params={params}
        subtotalLabelFormatPrefix={subtotalLabelFormatPrefix}
        subtotalLabelFormatSuffix={subtotalLabelFormatSuffix}
      />
    );
  }
  return null;
};

const renderMultipleGroupedColumns = (
  params: GridRenderCellParams<TabularRow, TabularDataCell>,
  subtotalLabelPosition: TabularSubtotalLabelPosition,
  subtotalLabelFormatPrefix: string,
  subtotalLabelFormatSuffix: string
) => {
  if (params.rowNode.type === "group") {
    if (params.value === undefined) {
      return null;
    }
    return <GroupingCell id={params.id} rowNode={params.rowNode} typographyProps={{ variant: "subtitle2" }} />;
  }
  if (params.rowNode.type === "leaf") {
    return <GroupingLeafCell cell={params.value} typographyProps={{ color: "textSecondary" }} />;
  }
  if (subtotalLabelPosition !== "None" && params.rowNode.type === "footer") {
    return (
      <MultipleColumnModeGroupingTotalCell
        params={params}
        subtotalLabelPosition={subtotalLabelPosition}
        subtotalLabelFormatPrefix={subtotalLabelFormatPrefix}
        subtotalLabelFormatSuffix={subtotalLabelFormatSuffix}
      />
    );
  }
  return null;
};
